import { Loader2, Mic, Volume2 } from "lucide-react";
import VoiceWaveform from "../VoiceWaveform";

const renderMicIcon = ({ processing, recording }) => {
  if (processing) return <Loader2 className="w-7 h-7 sm:w-8 sm:h-8 animate-spin" />;
  if (recording) return <span className="block w-5 h-5 sm:w-6 sm:h-6 rounded-sm bg-white" />;
  return <Mic className="w-7 h-7 sm:w-8 sm:h-8" />;
};

const processingLabel = (progressLabel) => {
  if (progressLabel === "transcribe") return "Transkribiere deine Stimme…";
  if (progressLabel === "thinking") return "KI denkt nach…";
  return "Verarbeite…";
};

const RecordingHint = ({ vadEnabled, vadState, silenceMs }) => {
  if (vadEnabled && vadState === "silence") {
    return (
      <p className="text-xs text-novatus-green mt-1 font-semibold" data-testid="vad-silence-hint">
        Stille erkannt · sende in {Math.max(0, Math.ceil((2000 - silenceMs) / 100) * 100)} ms…
      </p>
    );
  }
  if (vadEnabled && vadState === "speaking") {
    return (
      <p className="text-xs text-novatus-text-3 mt-1" data-testid="vad-speaking-hint">
        Spricht… auto-Stopp bei Pause
      </p>
    );
  }
  return <p className="text-xs text-novatus-text-3 mt-1">Tippe das Quadrat zum Senden</p>;
};

const VoiceStatus = ({ processing, aiSpeaking, recording, progressLabel, recordSecs, vadEnabled, vadState, silenceMs, onStopAi }) => {
  if (processing) {
    return (
      <p className="text-sm text-novatus-text-2 flex items-center justify-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-novatus-green" />
        {processingLabel(progressLabel)}
      </p>
    );
  }
  if (aiSpeaking) {
    return (
      <>
        <p className="text-novatus-green font-semibold text-sm sm:text-base flex items-center justify-center gap-2">
          <Volume2 className="w-4 h-4 animate-pulse" />
          KI spricht — bitte warten
        </p>
        <button
          type="button"
          onClick={onStopAi}
          className="text-xs text-novatus-text-3 hover:text-novatus-green mt-1"
          data-testid="stop-ai-audio-btn"
        >Stoppen & selbst sprechen</button>
      </>
    );
  }
  if (recording) {
    return (
      <>
        <p className="text-red-400 font-bold text-sm sm:text-base flex items-center justify-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          Aufnahme · <span className="font-mono">{String(Math.floor(recordSecs / 60)).padStart(2, "0")}:{String(recordSecs % 60).padStart(2, "0")}</span>
        </p>
        <RecordingHint vadEnabled={vadEnabled} vadState={vadState} silenceMs={silenceMs} />
      </>
    );
  }
  return (
    <>
      <p className="text-sm text-white font-semibold">Mikrofon antippen zum Sprechen</p>
      <p className="text-xs text-novatus-text-3 mt-1">
        {vadEnabled ? "Auto-Stopp bei Sprechpause · " : "Echo-Cancellation aktiv · "}
        <kbd className="px-1 py-0.5 rounded bg-novatus-card-2 border border-novatus text-novatus-text-2">Leertaste</kbd>
      </p>
    </>
  );
};

/** Voice mode UI: mic button, status text, VAD toggle, waveform, voice stats. */
const VoiceControls = ({
  voice,         // hook return from useVoiceRecording
  ending,
  lastVoiceStats,
}) => {
  const {
    recording, processing, recordSecs, micStream, aiSpeaking, progressLabel,
    vadEnabled, setVadEnabled, vadState, silenceMs,
    start, stop, cancel, stopAiAudio,
  } = voice;

  return (
    <div className="border-t border-novatus px-4 py-3 sm:p-6 flex flex-col items-center gap-2 sm:gap-4 shrink-0">
      <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
        {recording && (
          <span className="absolute inset-0 rounded-full border-2 border-red-500/70 pulse-green pointer-events-none" />
        )}
        <button
          type="button"
          onClick={() => { if (recording) stop(); else start(); }}
          disabled={processing || ending}
          aria-pressed={recording}
          title="Tippe einmal zum Sprechen. Auto-Stopp erkennt 2 Sek. Stille. Mit Leertaste auch per Tastatur."
          className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center transition-colors duration-150 select-none touch-none disabled:opacity-50 disabled:cursor-not-allowed ${
            recording ? "bg-red-500 hover:bg-red-600 text-white" : "bg-novatus-green hover:bg-[#25E058] text-black"
          }`}
          style={{ WebkitTapHighlightColor: "transparent" }}
          data-testid="voice-record-btn"
        >
          {renderMicIcon({ processing, recording })}
        </button>
      </div>

      <div className="text-center min-h-[36px] sm:min-h-[44px]">
        <VoiceStatus
          processing={processing}
          aiSpeaking={aiSpeaking}
          recording={recording}
          progressLabel={progressLabel}
          recordSecs={recordSecs}
          vadEnabled={vadEnabled}
          vadState={vadState}
          silenceMs={silenceMs}
          onStopAi={stopAiAudio}
        />
      </div>

      {!recording && !processing && (
        <button
          type="button"
          onClick={() => setVadEnabled(v => !v)}
          className={`text-[11px] px-3 py-1.5 rounded-full border transition-colors ${
            vadEnabled
              ? "bg-novatus-green/15 border-novatus-green/40 text-novatus-green"
              : "bg-novatus-card-2 border-novatus text-novatus-text-3 hover:text-white"
          }`}
          data-testid="vad-toggle-btn"
          aria-pressed={vadEnabled}
        >
          {vadEnabled ? "● Auto-Stopp aktiv" : "○ Auto-Stopp aus"}
        </button>
      )}

      {recording && micStream && (
        <div className="hidden sm:block w-full max-w-md px-2 animate-slide-up" data-testid="waveform-wrapper">
          <div className="rounded-xl bg-novatus-card-2 border border-novatus px-3 py-2">
            <VoiceWaveform stream={micStream} active={recording} color="#1FCB4F" bars={28} height={48} />
          </div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-novatus-text-3 text-center mt-2 font-semibold">Live-Pegel · sprich deutlich</p>
        </div>
      )}

      {recording && (
        <button
          type="button"
          onClick={cancel}
          className="text-xs text-novatus-text-3 hover:text-red-400 transition-colors px-3 py-1.5 rounded-md border border-novatus hover:border-red-500/40"
          data-testid="voice-cancel-btn"
        >
          Aufnahme verwerfen
        </button>
      )}

      {lastVoiceStats && !recording && !processing && (
        <div className="hidden sm:grid w-full max-w-md grid-cols-4 gap-2 animate-slide-up" data-testid="voice-stats-pill">
          <div className="text-center p-2 rounded-lg bg-novatus-card-2 border border-novatus">
            <div className="font-display font-black text-base text-novatus-green">{lastVoiceStats.wpm}</div>
            <div className="text-[9px] uppercase tracking-wider text-novatus-text-3 mt-0.5">WPM</div>
          </div>
          <div className="text-center p-2 rounded-lg bg-novatus-card-2 border border-novatus">
            <div className="font-display font-black text-base text-white capitalize">{lastVoiceStats.pacing}</div>
            <div className="text-[9px] uppercase tracking-wider text-novatus-text-3 mt-0.5">Tempo</div>
          </div>
          <div className="text-center p-2 rounded-lg bg-novatus-card-2 border border-novatus">
            <div className="font-display font-black text-base text-white">{lastVoiceStats.pause_count}</div>
            <div className="text-[9px] uppercase tracking-wider text-novatus-text-3 mt-0.5">Pausen</div>
          </div>
          <div className="text-center p-2 rounded-lg bg-novatus-card-2 border border-novatus">
            <div className={`font-display font-black text-base ${lastVoiceStats.filler_count > 3 ? "text-amber-400" : "text-white"}`}>{lastVoiceStats.filler_count}</div>
            <div className="text-[9px] uppercase tracking-wider text-novatus-text-3 mt-0.5">Füller</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceControls;
